import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Trash2, Plus, Star, Cog, Search as SearchIcon } from "lucide-react";

const empty = {
  name: "",
  supplier_id: "",
  role: "",
  phone: "",
  email: "",
  after_hours_phone: "",
  address: "",
  notes: "",
  specialities: [],
};

/**
 * Manager-only — create or edit a Service Technician.
 * "Supplier / Company" is picked from the Suppliers list (v79: orgs and
 * suppliers were merged so a single record holds machinery source + tech
 * employer + consumables vendor for a given company).
 */
const TechnicianFormDialog = ({
  open,
  initial,
  organisations,
  onClose,
  onSaved,
  onDelete,
  onAddOrganisation,
}) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [specDraft, setSpecDraft] = useState("");
  // Managed specialities list — powers the <datalist> suggestions on the
  // input, so staff pick from the curated set (no typos) but can still
  // type a fresh one if the list doesn't have it yet.
  const [managedSpecs, setManagedSpecs] = useState([]);
  // Machinery link picker — mirrors the picker on MachineFormDialog so the
  // user can link machines from either direction. Lazy-fetched on open.
  const [machines, setMachines] = useState([]);
  const [links, setLinks] = useState([]); // [{machine_id, is_primary, last_service_at}]
  // Bulk picker search — filters the tickbox list below.
  const [pickerSearch, setPickerSearch] = useState("");

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              ...empty,
              ...initial,
              supplier_id: initial.supplier_id || "",
              specialities: initial.specialities || [],
            }
          : empty,
      );
      setSpecDraft("");
      // Pull the managed specialities list so the datalist auto-completes
      // known ones. Non-manager users still get suggestions (read is open).
      api
        .get("/technician-specialities")
        .then(({ data }) => setManagedSpecs(data || []))
        .catch(() => setManagedSpecs([]));
      // Load machines + preseed link list from initial.linked_machines
      api
        .get("/machines")
        .then(({ data }) => setMachines(data || []))
        .catch(() => setMachines([]));
      if (initial?.id) {
        api
          .get(`/technicians/${initial.id}`)
          .then(({ data }) => {
            setLinks(
              (data.linked_machines || []).map((m) => ({
                machine_id: m.id,
                machine_name: m.name,
                is_primary: !!m.is_primary,
                last_service_at: m.last_service_at || null,
              })),
            );
          })
          .catch(() => setLinks([]));
      } else {
        setLinks([]);
      }
    }
  }, [open, initial]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Which machines are currently ticked (linked).
  const linkedIds = new Set(links.map((l) => l.machine_id));
  // Filter the tickbox list — case-insensitive match on name + category.
  const pickerRows = (machines || []).filter((m) => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (m.name || "").toLowerCase().includes(q) ||
      (m.category || "").toLowerCase().includes(q)
    );
  });
  // Bulk toggle: tick / untick a single machine from the picker.
  const toggleMachine = (m) => {
    setLinks((ls) => {
      if (ls.some((x) => x.machine_id === m.id)) {
        return ls.filter((x) => x.machine_id !== m.id);
      }
      return [
        ...ls,
        {
          machine_id: m.id,
          machine_name: m.name,
          is_primary: ls.length === 0,
          last_service_at: null,
        },
      ];
    });
  };
  // Tick every filtered machine that isn't already linked.
  const selectAllFiltered = () => {
    setLinks((ls) => {
      const cur = new Set(ls.map((x) => x.machine_id));
      const additions = pickerRows
        .filter((m) => !cur.has(m.id))
        .map((m, i) => ({
          machine_id: m.id,
          machine_name: m.name,
          is_primary: ls.length === 0 && i === 0,
          last_service_at: null,
        }));
      return [...ls, ...additions];
    });
  };
  const clearAllFiltered = () => {
    setLinks((ls) => {
      const filteredIds = new Set(pickerRows.map((m) => m.id));
      return ls.filter((l) => !filteredIds.has(l.machine_id));
    });
  };

  const addSpec = () => {
    const s = specDraft.trim();
    if (!s) return;
    if (form.specialities.some((x) => x.toLowerCase() === s.toLowerCase())) {
      setSpecDraft("");
      return;
    }
    set("specialities", [...form.specialities, s]);
    setSpecDraft("");
  };
  const removeSpec = (name) =>
    set(
      "specialities",
      form.specialities.filter((s) => s !== name),
    );

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const payload = { ...form, supplier_id: form.supplier_id || null };
      let saved;
      if (initial?.id) {
        const { data } = await api.put(`/technicians/${initial.id}`, payload);
        saved = data;
        toast.success("Technician updated");
      } else {
        const { data } = await api.post("/technicians", payload);
        saved = data;
        toast.success(`Added ${data.name}`);
      }
      // Persist machinery links via the tech-side endpoint so users can link
      // from either direction (mirrors PUT /machines/{id}/technicians).
      if (saved?.id) {
        try {
          await api.put(`/technicians/${saved.id}/machines`, {
            links: links.map((l) => ({
              machine_id: l.machine_id,
              is_primary: !!l.is_primary,
              last_service_at: l.last_service_at || null,
            })),
          });
        } catch (le) {
          toast.error(
            le.response?.data?.detail || "Tech saved but machine links failed",
          );
        }
      }
      onSaved?.(saved);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!initial?.id) return;
    if (!window.confirm(`Deactivate "${form.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/technicians/${initial.id}`);
      toast.success("Deactivated");
      onDelete?.(initial.id);
      onClose();
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="tech-form-overlay"
      onClick={onClose}
    >
      <div
        className="nb-card bg-white max-w-2xl w-full my-8 flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black flex-shrink-0 bg-white rounded-t-xl">
          <h2 className="font-display text-2xl">
            {initial ? "Edit technician" : "Add technician"}
          </h2>
          <button
            data-testid="tech-form-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
            aria-label="close"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 overflow-y-auto flex-1 min-h-0">
          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Name *</div>
            <input
              data-testid="tech-name-input"
              className="nb-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Marco Rossi"
              autoFocus
            />
          </label>
          <label className="sm:col-span-2">
            <div className="flex items-center justify-between mb-1">
              <div className="label-eyebrow">Supplier / Company</div>
              {onAddOrganisation && (
                <button
                  data-testid="tech-add-org-btn"
                  onClick={onAddOrganisation}
                  className="text-xs text-[#228EBF] font-bold hover:underline flex items-center gap-1"
                >
                  <Plus size={12} strokeWidth={2.5} /> New supplier
                </button>
              )}
            </div>
            <select
              data-testid="tech-org-select"
              className="nb-input"
              value={form.supplier_id}
              onChange={(e) => set("supplier_id", e.target.value)}
            >
              <option value="">— No company —</option>
              {(organisations || []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <div className="label-eyebrow mb-1">Role / title</div>
            <input
              data-testid="tech-role-input"
              className="nb-input"
              value={form.role || ""}
              onChange={(e) => set("role", e.target.value)}
              placeholder="Field Engineer"
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">Phone</div>
            <input
              data-testid="tech-phone-input"
              className="nb-input"
              value={form.phone || ""}
              onChange={(e) => set("phone", e.target.value)}
              type="tel"
              placeholder="+27 …"
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">Email</div>
            <input
              data-testid="tech-email-input"
              className="nb-input"
              value={form.email || ""}
              onChange={(e) => set("email", e.target.value)}
              type="email"
              placeholder="marco@axiom.co"
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">After-hours phone</div>
            <input
              data-testid="tech-after-hours-input"
              className="nb-input"
              value={form.after_hours_phone || ""}
              onChange={(e) => set("after_hours_phone", e.target.value)}
              type="tel"
            />
          </label>
          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Address</div>
            <input
              data-testid="tech-address-input"
              className="nb-input"
              value={form.address || ""}
              onChange={(e) => set("address", e.target.value)}
            />
          </label>
          <div className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Specialities</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.specialities.map((s) => (
                <span
                  key={s}
                  data-testid={`tech-spec-chip-${s}`}
                  className="inline-flex items-center gap-1 border-2 border-black rounded-full px-2 py-0.5 bg-[#EAF5FB] text-xs font-bold"
                >
                  {s}
                  <button
                    onClick={() => removeSpec(s)}
                    className="p-0.5 hover:bg-white rounded-full"
                    aria-label={`remove ${s}`}
                  >
                    <X size={10} strokeWidth={3} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="tech-spec-input"
                className="nb-input flex-1"
                list="tech-spec-suggestions"
                value={specDraft}
                onChange={(e) => setSpecDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addSpec();
                  }
                }}
                placeholder="Start typing — pick from list or add new"
              />
              <datalist id="tech-spec-suggestions">
                {managedSpecs
                  .filter(
                    (s) =>
                      !form.specialities.some(
                        (x) => x.toLowerCase() === s.name.toLowerCase(),
                      ),
                  )
                  .map((s) => (
                    <option key={s.id} value={s.name} />
                  ))}
              </datalist>
              <button
                data-testid="tech-spec-add-btn"
                onClick={addSpec}
                className="nb-btn bg-white px-3"
              >
                <Plus size={14} strokeWidth={2.5} />
              </button>
            </div>
            {managedSpecs.length > 0 && (
              <div className="label-eyebrow text-[#607087] mt-1 text-[10px]">
                Suggestions come from the admin-managed list.
                Add new names in <b>Technicians → Specialities</b> (manager only).
              </div>
            )}
          </div>
          <div className="sm:col-span-2 border-2 border-black rounded-xl p-3 bg-[#FFF9EC]">
            <div className="flex items-center gap-2 mb-2">
              <Cog size={14} strokeWidth={2.5} className="text-[#7A4A00]" />
              <div className="label-eyebrow">Services these machines</div>
              {links.length > 0 && (
                <span
                  data-testid="tech-machines-count-pill"
                  className="ml-auto inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full border-2 border-black bg-[#FFB300] text-[11px] font-bold"
                >
                  {links.length}
                </span>
              )}
            </div>
            {links.length > 0 && (
              <ul
                data-testid="tech-machines-linked-list"
                className="flex flex-col gap-1 mb-2 max-h-72 overflow-y-auto pr-1"
              >
                {links.map((l, i) => (
                  <li
                    key={l.machine_id}
                    data-testid={`tech-machine-row-${l.machine_id}`}
                    className="flex items-center gap-2 text-sm border-2 border-black rounded-lg px-2 py-1.5 bg-white flex-wrap"
                  >
                    <button
                      type="button"
                      data-testid={`tech-machine-primary-${l.machine_id}`}
                      onClick={() =>
                        setLinks((ls) =>
                          ls.map((x, j) => ({
                            ...x,
                            is_primary: j === i && !x.is_primary,
                          })),
                        )
                      }
                      title={
                        l.is_primary
                          ? "Primary contact for this machine"
                          : "Mark as primary for this machine"
                      }
                      className={`nb-btn p-1 ${
                        l.is_primary ? "bg-[#FFB300]" : "bg-white"
                      }`}
                    >
                      <Star
                        size={12}
                        strokeWidth={2.5}
                        fill={l.is_primary ? "#000" : "none"}
                      />
                    </button>
                    <span className="font-bold flex-1 min-w-0 truncate">
                      {l.machine_name ||
                        machines.find((m) => m.id === l.machine_id)?.name ||
                        l.machine_id}
                    </span>
                    <label className="flex items-center gap-1 text-[10px] text-[#607087]">
                      Last:
                      <input
                        type="date"
                        data-testid={`tech-machine-last-service-${l.machine_id}`}
                        className="nb-input py-0.5 px-1 text-xs w-32"
                        value={(l.last_service_at || "").slice(0, 10)}
                        onChange={(e) =>
                          setLinks((ls) =>
                            ls.map((x, j) =>
                              j === i
                                ? { ...x, last_service_at: e.target.value || null }
                                : x,
                            ),
                          )
                        }
                      />
                    </label>
                    <button
                      type="button"
                      data-testid={`tech-machine-remove-${l.machine_id}`}
                      onClick={() =>
                        setLinks((ls) => ls.filter((_, j) => j !== i))
                      }
                      className="nb-btn bg-white p-1"
                      aria-label="remove"
                    >
                      <Trash2 size={12} strokeWidth={2.5} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {/* Bulk tickbox picker — assign the tech to many machines in
                one go. Search filters the list; "Tick all" adds every filtered
                machine at once. Rows above show detail (primary + last
                service) once ticked. */}
            <div className="border-2 border-black rounded-lg bg-white overflow-hidden">
              <div className="flex items-center gap-2 border-b-2 border-black bg-white p-2">
                <div className="relative flex-1">
                  <SearchIcon
                    size={12}
                    strokeWidth={2.5}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-[#607087]"
                  />
                  <input
                    data-testid="tech-machine-picker-search"
                    className="nb-input pl-7 h-8 text-sm"
                    placeholder="Filter machines…"
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  data-testid="tech-machine-pick-all"
                  onClick={selectAllFiltered}
                  className="nb-btn bg-[#EAF5FB] px-2 py-1 text-xs font-bold"
                  title="Tick every machine in this filter"
                >
                  Tick all
                </button>
                <button
                  type="button"
                  data-testid="tech-machine-pick-clear"
                  onClick={clearAllFiltered}
                  className="nb-btn bg-white px-2 py-1 text-xs font-bold"
                  title="Untick every machine in this filter"
                >
                  Clear
                </button>
              </div>
              <div
                className="max-h-56 overflow-y-auto divide-y divide-black/10"
                data-testid="tech-machine-picker-list"
              >
                {pickerRows.length === 0 && (
                  <div className="p-3 text-xs text-[#607087] text-center">
                    {machines.length === 0
                      ? "No machines defined yet — add them in Machinery first."
                      : "No machines match this filter."}
                  </div>
                )}
                {pickerRows.map((m) => {
                  const on = linkedIds.has(m.id);
                  return (
                    <label
                      key={m.id}
                      data-testid={`tech-machine-tick-${m.id}`}
                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm ${
                        on ? "bg-[#FFF7D6]" : "hover:bg-black/5"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleMachine(m)}
                        className="w-4 h-4 accent-black"
                      />
                      <span className="font-bold flex-1 truncate">{m.name}</span>
                      {m.category && (
                        <span className="text-[10px] text-[#607087]">
                          {m.category}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              {links.length > 0 && (
                <div className="text-[10px] text-[#607087] p-2 border-t-2 border-black bg-[#FFF9EC]">
                  {links.length} machine{links.length === 1 ? "" : "s"} ticked ·
                  tap the star on any row above to mark this tech as the
                  primary contact for that machine.
                </div>
              )}
            </div>
          </div>

          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Notes</div>
            <textarea
              data-testid="tech-notes-input"
              className="nb-input min-h-[80px]"
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="e.g. Prefers WhatsApp · handles both machines but not calibration"
            />
          </label>
        </div>

        <div className="p-5 border-t-2 border-black flex items-center justify-between gap-2 flex-wrap flex-shrink-0 bg-white rounded-b-xl">
          {initial?.id ? (
            <button
              data-testid="tech-delete-btn"
              onClick={del}
              disabled={busy}
              className="nb-btn bg-white px-3 py-2 flex items-center gap-2 text-sm text-[#8A1F1F]"
            >
              <Trash2 size={14} strokeWidth={2.5} /> Delete
            </button>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="tech-cancel-btn"
              onClick={onClose}
              className="nb-btn bg-white px-4 py-2"
            >
              Cancel
            </button>
            <button
              data-testid="tech-save-btn"
              onClick={save}
              disabled={busy}
              className="nb-btn nb-btn-primary px-5 py-2 flex items-center gap-2 flex-1 sm:flex-none justify-center"
            >
              <Save size={14} strokeWidth={2.5} /> {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicianFormDialog;
